import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

/** User schema - represents authenticated user */
export const UserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  role: z.string()
});

/** API response schema for JWT tokens */
export const ApiAuthTokensSchema = z.object({
  access_token: z.string()
});

/** Transformed response schema for JWT tokens returned after successful authentication */
export const AuthTokensSchema = z.object({
  accessToken: z.string()
});

/**
 * Login with email and password request schema.
 * `gRecaptchaResponse` is the optional hidden reCAPTCHA token; `serializeRequestBody`
 * converts it to the API's native `g_recaptcha_response`.
 */
export const LoginParamsSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  rememberMe: z.boolean(),
  gRecaptchaResponse: z.string().optional()
});

/** Whether a password login or a two-factor verification signed the user in, or a code is still needed. */
export const AUTHENTICATION_STATUS = {
  AUTHENTICATED: 'authenticated',
  TWO_FACTOR_REQUIRED: 'two_factor_required'
} as const;

const ApiAuthenticatedResponseSchema = z
  .object({
    success: z.literal(true),
    authentication_status: z.literal(AUTHENTICATION_STATUS.AUTHENTICATED)
  })
  .merge(ApiAuthTokensSchema);

/** Signed in: the access token. */
export const AuthenticatedResponseSchema = ApiAuthenticatedResponseSchema.transform((data) => camelcaseKeys(data));

/** The account has two-factor authentication on: no tokens yet, only a challenge to send back with the code. */
export const TwoFactorChallengeResponseSchema = z
  .object({
    authentication_status: z.literal(AUTHENTICATION_STATUS.TWO_FACTOR_REQUIRED),
    /** Opaque, single-use token the code is sent with */
    challenge_token: z.string(),
    /** Seconds the challenge stays valid */
    expires_in: z.number()
  })
  .transform((data) => camelcaseKeys(data));

/** Validates and transforms the raw API response for login: signed in, or a two-factor challenge. */
export const LoginResponseSchema = z.union([TwoFactorChallengeResponseSchema, AuthenticatedResponseSchema]);

/** The two-factor code for a login challenge. */
export const VerifyTwoFactorParamsSchema = z.object({
  challengeToken: z.string(),
  /**
   * Six letters or digits: an authenticator app code, or an unused backup code (case-sensitive).
   * A string, so leading zeroes stay.
   */
  code: z.string()
});

/** A verified two-factor code signs the user in, with the same answer as a one-step login. */
export const VerifyTwoFactorResponseSchema = AuthenticatedResponseSchema;

/** Error codes of a two-factor login that need their own handling. */
export const TWO_FACTOR_ERROR_CODE = {
  /** 401 from verify: the code is wrong; the user can try another one */
  WRONG_CODE: 'WRONG_2FA_CODE',
  /** 401 from verify: the challenge expired or was used up; the user logs in again */
  INVALID_CHALLENGE: 'INVALID_2FA_CHALLENGE',
  /** 429 from login or verify: too many wrong codes; the user waits `retryAfter` seconds */
  ATTEMPTS_EXCEEDED: 'TWO_FACTOR_ATTEMPTS_EXCEEDED'
} as const;

/** The error body of a two-factor login failure: its code and, on a 429, the seconds to wait. */
export const TwoFactorErrorBodySchema = z
  .object({
    errors: z.union([
      z.object({
        code: z.literal(TWO_FACTOR_ERROR_CODE.ATTEMPTS_EXCEEDED),
        retry_after: z.number()
      }),
      z.object({
        code: z.enum([TWO_FACTOR_ERROR_CODE.WRONG_CODE, TWO_FACTOR_ERROR_CODE.INVALID_CHALLENGE])
      })
    ])
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

/**
 * Optional metadata sent with sign-up requests.
 * Keys are camelCase at the type boundary; conversion to the API's native
 * casing (snake_case) happens in the api method via `serializeRequestBody`.
 */
export const MetaSchema = z.object({ subscriptionId: z.string() }).optional();

/** Sign-up with email and password request schema */
export const EmailPasswordSignUpParamsSchema = z.object({
  userEmail: z.string().email(),
  userPassword: z.string().min(1),
  userMarketingOptIn: z.boolean(),
  customerPrivacyOptIn: z.boolean(),
  gRecaptchaResponse: z.string().nullable(),
  meta: MetaSchema
});

/** Transformed response schema for email/password sign-up */
export const EmailPasswordSignUpResponseSchema = ApiAuthTokensSchema.transform((data) => camelcaseKeys(data)).pipe(
  AuthTokensSchema
);

/**
 * Google sign-in request schema (used for both sign-up and login).
 *
 * Fields are camelCase at the type boundary; conversion to the API's native
 * casing (snake_case) happens in the api method via `serializeRequestBody`.
 */
export const GoogleSignInParamsSchema = z.object({
  token: z.string(),
  meta: MetaSchema
});

/** Success response schema for Google sign-in (includes sign-up and login) */
export const GoogleSignInResponseSchema = z
  .object({
    email: z.string().email(),
    first_name: z.string().optional(),
    last_name: z.string().optional()
  })
  .merge(ApiAuthTokensSchema)
  .transform((data) => camelcaseKeys(data));

/** Transformed response schema for POST /auth/v1/jwt/access-token-refresh. */
export const AccessTokenRefreshResponseSchema = ApiAuthTokensSchema.transform((data) => camelcaseKeys(data)).pipe(
  AuthTokensSchema
);

/** Response schema for POST /auth/v1/logout */
export const LogoutResponseSchema = z.object({}).passthrough();

/** Forgot-password request: email + reCAPTCHA token */
export const ForgotPasswordParamsSchema = z.object({
  email: z.string().email(),
  gRecaptchaResponse: z.string()
});

/** Response confirming the password-reset email was dispatched */
export const ForgotPasswordResponseSchema = z.object({
  sent: z.boolean()
});

/** Reset-password request: email, reset code, new password with confirmation */
export const ResetPasswordParamsSchema = z.object({
  email: z.string().email(),
  code: z.string().min(1),
  newPassword: z.string().min(1),
  passwordConfirmation: z.string().min(1)
});

/** Response confirming the password was successfully reset */
export const ResetPasswordResponseSchema = z.object({
  success: z.boolean()
});

/** Send-email-verification-code request: email + reCAPTCHA token */
export const SendEmailVerificationCodeParamsSchema = z.object({
  email: z.string().email(),
  gRecaptchaResponse: z.string()
});

/**
 * Response confirming the verification email was dispatched.
 * Always `true` on 200 — the API reports a send for unknown and already-verified
 * addresses alike so the response can't be used to enumerate users.
 */
export const SendEmailVerificationCodeResponseSchema = z.object({
  sent: z.boolean()
});

/** Verify-email request: email + the code from the verification email */
export const VerifyEmailParamsSchema = z.object({
  email: z.string().email(),
  code: z.string().min(1)
});

/** Response confirming the email address was verified */
export const VerifyEmailResponseSchema = z.object({
  success: z.boolean()
});

export type AuthTokens = z.infer<typeof AuthTokensSchema>;
export type LoginParams = z.infer<typeof LoginParamsSchema>;
export type LoginResponse = z.infer<typeof LoginResponseSchema>;
export type TwoFactorChallengeResponse = z.infer<typeof TwoFactorChallengeResponseSchema>;
export type VerifyTwoFactorParams = z.infer<typeof VerifyTwoFactorParamsSchema>;
export type VerifyTwoFactorResponse = z.infer<typeof VerifyTwoFactorResponseSchema>;
export type ForgotPasswordParams = z.infer<typeof ForgotPasswordParamsSchema>;
export type ForgotPasswordResponse = z.infer<typeof ForgotPasswordResponseSchema>;
export type ResetPasswordParams = z.infer<typeof ResetPasswordParamsSchema>;
export type ResetPasswordResponse = z.infer<typeof ResetPasswordResponseSchema>;
export type SendEmailVerificationCodeParams = z.infer<typeof SendEmailVerificationCodeParamsSchema>;
export type SendEmailVerificationCodeResponse = z.infer<typeof SendEmailVerificationCodeResponseSchema>;
export type VerifyEmailParams = z.infer<typeof VerifyEmailParamsSchema>;
export type VerifyEmailResponse = z.infer<typeof VerifyEmailResponseSchema>;
export type EmailPasswordSignUpParams = z.infer<typeof EmailPasswordSignUpParamsSchema>;
export type EmailPasswordSignUpResponse = z.infer<typeof EmailPasswordSignUpResponseSchema>;
export type GoogleSignInParams = z.infer<typeof GoogleSignInParamsSchema>;
export type GoogleSignInResponse = z.infer<typeof GoogleSignInResponseSchema>;
export type LogoutResponse = z.infer<typeof LogoutResponseSchema>;
export type Meta = z.infer<typeof MetaSchema>;

export type User = z.infer<typeof UserSchema>;
